import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { FileSpreadsheet, Search, Loader2, ChevronLeft, ChevronRight, MoreVertical, Eye, Copy, Download, ChevronDown, X, AlertTriangle, RefreshCw } from 'lucide-react';
import { PayoutDetailsModal } from '../components/payouts/PayoutDetailsModal';
import { BulkActionDialog } from '../components/payouts/BulkActionDialog';
import { BulkSettlementModal } from '../components/payouts/BulkSettlementModal';
import { ExcelSettlementDialog } from '../components/payouts/ExcelSettlementDialog';
import { TABS, ACTIONS, BULK_ACTION_KEYS, statusOf, formatINR, fmtDate, fmtTime, maskAcct, copyPayoutDetails } from '../components/payouts/payoutUtils';

const StatusBadge = ({ status }) => {
  const s = statusOf(status);
  return <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-sm border whitespace-nowrap ${s.cls}`} data-testid={`status-badge-${status}`}><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}</span>;
};

const PAGE_SIZES = [25, 50, 100];

export default function AdminPayoutsPage() {
  const { api } = useAuth();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(location.pathname.endsWith('/pending') ? 'pending' : 'all');
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [filters, setFilters] = useState({ merchant_id: 'all', bank: 'all', date_from: '', date_to: '', amount_min: '', amount_max: '' });
  const [options, setOptions] = useState({ merchants: [], banks: [], status_counts: {} });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState({});
  const [drawerId, setDrawerId] = useState(null);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkSettleOpen, setBulkSettleOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [excelOpen, setExcelOpen] = useState(false);

  useEffect(() => { const t = setTimeout(() => setQDebounced(q.trim()), 350); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [tab, qDebounced, filters, pageSize]);

  const fetchOptions = useCallback(async () => {
    try { const r = await api.get('/payouts/admin/filters'); setOptions(r.data); } catch { /* non-blocking */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const t = TABS.find(x => x.key === tab);
      const params = new URLSearchParams({ limit: String(pageSize), skip: String((page - 1) * pageSize) });
      if (t?.statuses) params.set('status', t.statuses.join(','));
      if (qDebounced) params.set('q', qDebounced);
      if (filters.merchant_id !== 'all') params.set('merchant_id', filters.merchant_id);
      if (filters.bank !== 'all') params.set('bank', filters.bank);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      if (filters.amount_min) params.set('amount_min', filters.amount_min);
      if (filters.amount_max) params.set('amount_max', filters.amount_max);
      const r = await api.get(`/payouts/admin/all?${params.toString()}`);
      setRows(r.data.payouts || []); setTotal(r.data.total || 0);
    } catch { toast.error('Failed to load payouts'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, qDebounced, filters, page, pageSize]);

  useEffect(() => { setTab(location.pathname.endsWith('/pending') ? 'pending' : 'all'); }, [location.pathname]);
  useEffect(() => { fetchOptions(); }, [fetchOptions]);
  useEffect(() => { fetchRows(); }, [fetchRows]);
  const refresh = () => { fetchRows(); fetchOptions(); };

  const selectedRows = useMemo(() => rows.filter(r => selected[r.id]), [rows, selected]);
  const selectedIds = Object.keys(selected).filter(k => selected[k]);
  const allVisibleSelected = rows.length > 0 && rows.every(r => selected[r.id]);
  const toggleAll = (on) => setSelected(prev => { const n = { ...prev }; rows.forEach(r => { if (on) n[r.id] = r; else delete n[r.id]; }); return n; });
  const toggleOne = (r, on) => setSelected(prev => { const n = { ...prev }; if (on) n[r.id] = r; else delete n[r.id]; return n; });
  const clearSelection = () => setSelected({});
  const allSelected = Object.values(selected);
  const eligibleCount = (k) => allSelected.filter(p => ACTIONS[k].from.includes(p.status)).length;

  const exportSelected = async () => {
    try {
      const r = await api.post('/payouts/admin/export', { payoutIds: allSelected.map(p => p.id) }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a');
      a.href = url; a.download = `payouts_export_${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success(`Exported ${allSelected.length} payouts`);
    } catch { toast.error('Export failed'); }
  };

  const counts = options.status_counts || {};
  const tabCount = (t) => t.statuses ? t.statuses.reduce((s, k) => s + (counts[k] || 0), 0) : counts.all;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const activeFilterCount = Object.entries(filters).filter(([k, v]) => v && v !== 'all').length;

  return (
    <div className="space-y-4" data-testid="admin-payouts-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold">Payouts</h1>
          <p className="text-sm text-muted-foreground">High-volume payout operations · {counts.all ?? 0} orders · {counts.pending_admin_review ?? 0} awaiting approval</p>
        </div>
        <div className="flex gap-2 self-start">
          <Button size="sm" onClick={() => setExcelOpen(true)} className="rounded-sm" data-testid="excel-settlement-btn"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Excel Settlement</Button>
          <Button variant="outline" size="sm" onClick={refresh} className="rounded-sm" data-testid="refresh-btn"><RefreshCw className="h-4 w-4 mr-1.5" />Refresh</Button>
        </div>
      </div>

      {/* Status tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-border/60" data-testid="status-tabs">
        {TABS.map(t => {
          const n = tabCount(t); const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => { setTab(t.key); }} data-testid={`tab-${t.key}`}
              className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${active ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
              {t.label}{n > 0 && <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-sm ${active ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>{n}</span>}
            </button>
          );
        })}
      </div>

      {/* Search + filters */}
      <div className="flex flex-col lg:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by Order ID, merchant, MID, beneficiary, email or UTR" className="pl-8 h-9 rounded-sm" data-testid="payout-search" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={filters.merchant_id} onValueChange={v => setFilters(f => ({ ...f, merchant_id: v }))}>
            <SelectTrigger className="h-9 w-[180px] rounded-sm text-xs" data-testid="filter-merchant"><SelectValue placeholder="Merchant" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All merchants</SelectItem>{options.merchants.map(m => <SelectItem key={m.id} value={m.id}>{m.business_name} <span className="text-muted-foreground font-mono text-[10px]">{m.mid}</span></SelectItem>)}</SelectContent>
          </Select>
          {options.banks.length > 0 && (
            <Select value={filters.bank} onValueChange={v => setFilters(f => ({ ...f, bank: v }))}>
              <SelectTrigger className="h-9 w-[150px] rounded-sm text-xs" data-testid="filter-bank"><SelectValue placeholder="Bank" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All banks</SelectItem>{options.banks.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" className="h-9 rounded-sm text-xs" onClick={() => setShowFilters(s => !s)} data-testid="toggle-filters-btn">
            Date & Amount{activeFilterCount > 0 && <span className="ml-1.5 bg-primary text-primary-foreground text-[10px] px-1.5 rounded-sm">{activeFilterCount}</span>}<ChevronDown className={`h-3.5 w-3.5 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </Button>
          {(activeFilterCount > 0 || q) && <Button variant="ghost" size="sm" className="h-9 rounded-sm text-xs" onClick={() => { setFilters({ merchant_id: 'all', bank: 'all', date_from: '', date_to: '', amount_min: '', amount_max: '' }); setQ(''); }} data-testid="clear-filters-btn"><X className="h-3.5 w-3.5 mr-1" />Clear</Button>}
        </div>
      </div>
      {showFilters && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 rounded-sm border border-border/60 bg-card/50" data-testid="advanced-filters">
          {[['date_from', 'From date', 'date'], ['date_to', 'To date', 'date'], ['amount_min', 'Min amount (₹)', 'number'], ['amount_max', 'Max amount (₹)', 'number']].map(([k, l, type]) => (
            <div key={k} className="space-y-1"><label className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</label><Input type={type} value={filters[k]} onChange={e => setFilters(f => ({ ...f, [k]: e.target.value }))} className="h-8 rounded-sm text-xs" data-testid={`filter-${k}`} /></div>
          ))}
        </div>
      )}

      {/* Bulk toolbar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 px-3 py-2 rounded-sm border border-primary/30 bg-primary/5" data-testid="bulk-toolbar">
          <span className="text-sm font-medium" data-testid="bulk-selected-label">{selectedIds.length} selected</span>
          <span className="text-xs text-muted-foreground hidden sm:inline">· {formatINR(allSelected.reduce((s, p) => s + (p.amount || 0), 0))}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button size="sm" className="rounded-sm h-8" data-testid="bulk-actions-btn">Bulk Actions <ChevronDown className="h-3.5 w-3.5 ml-1" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-60 rounded-sm">
              <DropdownMenuItem onClick={() => copyPayoutDetails(api, allSelected.map(p => p.id), toast)} data-testid="bulk-copy"><Copy className="h-4 w-4 mr-2" />Copy Details</DropdownMenuItem>
              <DropdownMenuSeparator />
              {BULK_ACTION_KEYS.map(k => {
                const n = eligibleCount(k);
                return (
                  <DropdownMenuItem key={k} disabled={n === 0} onClick={() => (k === 'paid' ? setBulkSettleOpen(true) : setBulkAction(k))} data-testid={`bulk-${k}`} className="flex justify-between">
                    <span>{ACTIONS[k].label}{k === 'paid' && <span className="block text-[10px] text-muted-foreground">Proof required</span>}</span><span className="text-[10px] text-muted-foreground">{n} eligible</span>
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={exportSelected} data-testid="bulk-export"><Download className="h-4 w-4 mr-2" />Export CSV</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="sm" className="rounded-sm h-8 ml-auto text-xs" onClick={clearSelection} data-testid="clear-selection-btn"><X className="h-3.5 w-3.5 mr-1" />Clear</Button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-sm border border-border/60 bg-card overflow-x-auto" data-testid="payouts-table-wrap">
        <Table className="min-w-[900px]">
          <TableHeader className="sticky top-0 bg-card z-[1]">
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-10 h-9"><Checkbox checked={allVisibleSelected} onCheckedChange={toggleAll} aria-label="Select all" data-testid="select-all-checkbox" /></TableHead>
              <TableHead className="h-9 text-xs">Order ID</TableHead>
              <TableHead className="h-9 text-xs">Merchant</TableHead>
              <TableHead className="h-9 text-xs">Beneficiary</TableHead>
              <TableHead className="h-9 text-xs text-right">Amount</TableHead>
              <TableHead className="h-9 text-xs">Date & Time</TableHead>
              <TableHead className="h-9 text-xs">Status</TableHead>
              <TableHead className="h-9 text-xs text-right w-20">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && rows.length === 0 && <TableRow><TableCell colSpan={8} className="h-32 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading payouts…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={8} className="h-32 text-center text-muted-foreground" data-testid="empty-state">No payouts match the current filters.</TableCell></TableRow>}
            {rows.map(p => (
              <TableRow key={p.id} className={`h-12 ${selected[p.id] ? 'bg-primary/5' : ''}`} data-testid={`payout-row-${p.order_id}`}>
                <TableCell className="py-1.5"><Checkbox checked={!!selected[p.id]} onCheckedChange={(c) => toggleOne(p, !!c)} aria-label={`Select ${p.order_id}`} data-testid={`select-${p.order_id}`} /></TableCell>
                <TableCell className="py-1.5">
                  <button onClick={() => setDrawerId(p.id)} className="font-mono text-sm font-medium text-primary hover:underline inline-flex items-center gap-1" data-testid={`order-link-${p.order_id}`}>
                    {p.order_id}{p.is_flagged && <AlertTriangle className="h-3 w-3 text-amber-500" />}
                  </button>
                </TableCell>
                <TableCell className="py-1.5"><p className="text-sm leading-tight">{p.merchant?.business_name || '—'}</p><p className="text-[11px] text-muted-foreground font-mono">{p.merchant?.mid || ''}</p></TableCell>
                <TableCell className="py-1.5"><p className="text-sm leading-tight truncate max-w-[200px]">{p.beneficiary_name}</p><p className="text-[11px] text-muted-foreground">{p.bank_name} · {maskAcct(p.account_number_masked)}</p></TableCell>
                <TableCell className="py-1.5 text-right font-mono text-sm font-medium" data-testid={`amount-${p.order_id}`}>{formatINR(p.amount)}</TableCell>
                <TableCell className="py-1.5"><p className="text-sm leading-tight">{fmtDate(p.created_at)}</p><p className="text-[11px] text-muted-foreground">{fmtTime(p.created_at)}</p></TableCell>
                <TableCell className="py-1.5"><StatusBadge status={p.status} /></TableCell>
                <TableCell className="py-1.5 text-right">
                  <div className="inline-flex items-center gap-0.5">
                    <Button variant="ghost" size="sm" className="h-7 px-2 rounded-sm text-xs" onClick={() => setDrawerId(p.id)} data-testid={`view-${p.order_id}`}><Eye className="h-3.5 w-3.5 mr-1" />View</Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-sm" data-testid={`row-menu-${p.order_id}`}><MoreVertical className="h-3.5 w-3.5" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-sm">
                        <DropdownMenuItem onClick={() => setDrawerId(p.id)} data-testid={`menu-view-${p.order_id}`}><Eye className="h-4 w-4 mr-2" />View Details</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => copyPayoutDetails(api, [p.id], toast)} data-testid={`menu-copy-${p.order_id}`}><Copy className="h-4 w-4 mr-2" />Copy Details</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm" data-testid="pagination">
        <div className="flex items-center gap-3 text-muted-foreground">
          <span data-testid="pagination-summary">Showing {from}–{to} of {total.toLocaleString('en-IN')} payouts</span>
          <Select value={String(pageSize)} onValueChange={v => setPageSize(Number(v))}>
            <SelectTrigger className="h-8 w-[90px] rounded-sm text-xs" data-testid="page-size-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PAGE_SIZES.map(n => <SelectItem key={n} value={String(n)}>{n} rows</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-8 rounded-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page-btn"><ChevronLeft className="h-4 w-4" />Previous</Button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const start = Math.max(1, Math.min(page - 2, totalPages - 4)); const n = start + i;
            return <Button key={n} variant={n === page ? 'default' : 'ghost'} size="sm" className="h-8 w-8 p-0 rounded-sm" onClick={() => setPage(n)} data-testid={`page-${n}`}>{n}</Button>;
          })}
          <Button variant="outline" size="sm" className="h-8 rounded-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page-btn">Next<ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <PayoutDetailsModal payoutId={drawerId} open={!!drawerId} onOpenChange={(o) => !o && setDrawerId(null)} onChanged={refresh} />
      <ExcelSettlementDialog open={excelOpen} onOpenChange={setExcelOpen} onDone={refresh} />
      <BulkActionDialog action={bulkAction} payouts={allSelected} open={!!bulkAction} onOpenChange={(o) => !o && setBulkAction(null)} onDone={() => { clearSelection(); refresh(); }} />
      <BulkSettlementModal payouts={allSelected} open={bulkSettleOpen} onOpenChange={setBulkSettleOpen} onDone={() => { clearSelection(); refresh(); }} />
    </div>
  );
}
